import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionHead } from "@/lib/motion";
import { FAQS, CHECKLIST_GROUPS } from "@/data/content";
import { ARTICLES } from "@/data/articles";
import { fetchFees } from "@/lib/api";
import { computeFeeTiers } from "@/lib/priceTier";
import TierMeter from "@/components/TierMeter";

function Checklist() {
  const [checked, setChecked] = useState({});
  const [fees, setFees] = useState(null);

  useEffect(() => {
    fetchFees().then(setFees).catch(() => setFees(null));
  }, []);

  const feeFor = (id) => (fees?.items || []).find((f) => f.id === id);
  const feeTiers = useMemo(() => (fees?.items ? computeFeeTiers(fees.items) : {}), [fees]);

  const personalGroup = CHECKLIST_GROUPS.find((g) => g.id === "personal");
  const otherGroups = CHECKLIST_GROUPS.filter((g) => g.id !== "personal");

  const personalTotal = personalGroup?.items.length || 0;
  const personalDone = (personalGroup?.items || []).filter((_, i) => checked[`personal-${i}`]).length;
  const personalPct = personalTotal ? Math.round((personalDone / personalTotal) * 100) : 0;

  // Complexity & cost read comes straight from the fee data — the highest
  // complexity and price tier among the forms the client has checked, exactly
  // as the Pricing page derives its overall read.
  const checkedFeeIds = otherGroups.flatMap((g) =>
    g.items.map((item, i) => (checked[`${g.id}-${i}`] ? item.fee : null)).filter(Boolean),
  );
  const complexityLevel = checkedFeeIds.reduce(
    (m, id) => Math.max(m, Number(feeFor(id)?.complexity) || 0),
    0,
  );
  const costLevel = checkedFeeIds.reduce((m, id) => Math.max(m, feeTiers[id] || 0), 0);

  return (
    <div data-testid="document-checklist">
      <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border border-steel-border bg-steel-surface p-6" data-testid="checklist-personal-progress">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-steel-muted">Personal information</span>
            <span className="font-mono text-sm text-steel-brass" data-testid="checklist-personal-label">
              {personalDone} / {personalTotal}
            </span>
          </div>
          <div className="mt-4 h-1 w-full bg-steel-elevated">
            <div
              data-testid="checklist-personal-bar"
              className="h-1 bg-steel-brass transition-all duration-500 shadow-[0_0_14px_rgba(168,85,247,0.7)]"
              style={{ width: `${personalPct}%` }}
            />
          </div>
          <p className="mt-4 text-xs text-steel-muted">
            The baseline everyone needs. Check off what you already have on hand.
          </p>
        </div>
        <div className="border border-steel-border bg-steel-surface p-6" data-testid="checklist-cost-complexity">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-steel-muted">Complexity &amp; cost read</span>
          <div className="mt-4 flex flex-col gap-3">
            <span className="flex items-center gap-3" data-testid="checklist-complexity-meter">
              <span className="w-20 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Complexity</span>
              <TierMeter kind="complexity" value={complexityLevel} showLabel={false} />
            </span>
            <span className="flex items-center gap-3" data-testid="checklist-cost-meter">
              <span className="w-20 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Cost</span>
              <TierMeter kind="price" value={costLevel} showLabel={false} />
            </span>
          </div>
          <p className="mt-4 text-xs text-steel-muted">
            We charge by the forms your return needs — this reflects the highest complexity and cost among the forms you've checked.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {CHECKLIST_GROUPS.map((g) => (
          <div key={g.id} className="border border-steel-border bg-steel-surface p-8">
            <h3 className="font-display text-xl font-medium text-steel-text">{g.title}</h3>
            <ul className="mt-6 space-y-3">
              {g.items.map((item, i) => {
                const key = `${g.id}-${i}`;
                const on = !!checked[key];
                return (
                  <li key={key}>
                    <button
                      type="button"
                      data-testid={`checklist-item-${key}`}
                      onClick={() => setChecked((c) => ({ ...c, [key]: !on }))}
                      className="group flex w-full items-start gap-3 text-left"
                    >
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors duration-300 ${
                          on ? "border-steel-brass bg-steel-brass/20" : "border-steel-border group-hover:border-steel-muted"
                        }`}
                      >
                        {on && <Check className="h-3.5 w-3.5 text-steel-brass" />}
                      </span>
                      <span className={`text-sm leading-relaxed transition-colors duration-300 ${on ? "text-steel-muted line-through" : "text-steel-secondary"}`}>
                        {item.t}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Resources() {
  return (
    <div data-testid="resources-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHead
            overline="Resources"
            title="Answers before you even ask."
            copy="Honest pricing talk, the full document checklist, and plain-English articles on the situations we see most."
          />
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="scroll-mt-28 border-b border-steel-border">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHead overline="Pricing, honestly" title="What does tax preparation cost?" />
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-base leading-relaxed text-steel-secondary sm:text-lg">
                Tax preparation fees depend on the forms and complexity involved in your return. A W-2-only
                return generally requires less preparation than a return involving self-employment, rental
                properties, investments, multiple states or business ownership.
              </p>
              <p className="mt-6 text-base leading-relaxed text-steel-secondary sm:text-lg">
                What we will not do is hide behind “contact us for pricing.” Tell us your situation and you
                get a clear quote before any work begins. The Complexity Check gives you an instant read in
                about a minute.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/get-started?type=estimate"
                  data-testid="pricing-estimate-btn"
                  className="flex items-center justify-center gap-2 border border-steel-border px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-surface"
                >
                  Get a Preparation Estimate
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section id="checklist" className="scroll-mt-28 border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <SectionHead
            overline="Interactive checklist"
            title="What documents do I need?"
            copy="Work through the list. Check what you have; what remains is your gathering list."
          />
          <div className="mt-14">
            <Checklist />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-28 border-b border-steel-border">
        <div className="mx-auto max-w-5xl px-6 py-24 lg:px-10">
          <SectionHead overline="FAQ" title="The questions everyone actually asks." />
          <Reveal className="mt-14">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-steel-border">
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="py-6 text-left font-display text-lg font-medium text-steel-text hover:text-steel-brass hover:no-underline sm:text-xl"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`faq-answer-${i}`} className="pb-6 text-base leading-relaxed text-steel-secondary">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ARTICLES */}
      <section id="articles" className="scroll-mt-28">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <SectionHead
            overline="Tax Resource Center"
            title="Plain-English guides."
            copy="Written the way we explain things to clients — no jargon, no fluff."
          />
          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {ARTICLES.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.07}>
                <Link
                  to={`/resources/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group flex h-full flex-col justify-between border border-steel-border bg-steel-surface p-8 transition-all duration-300 hover:-translate-y-1 hover:border-steel-brass/50"
                >
                  <div>
                    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
                      <span className="text-steel-brass">{a.category}</span>
                      <span>·</span>
                      <span>{a.minutes} min read</span>
                    </div>
                    <h3 className="mt-4 font-display text-xl font-medium leading-snug text-steel-text">{a.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-steel-secondary">{a.excerpt}</p>
                  </div>
                  <span className="mt-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-steel-muted transition-colors duration-300 group-hover:text-steel-brass">
                    Read <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
